import {
  AccessPoint,
  ECamType,
  TCameraDevice,
} from '../entities/access-edges.interface';

/** A webcam bound to an access point, addressed by the edge that holds it. */
export type TWebcamRelease = {
  edgeId: string;
  deviceName: string;
};

const isActiveWebcam = (
  cameraDevice: TCameraDevice | null | undefined
): boolean =>
  cameraDevice?.IsActive === true &&
  cameraDevice?.Type === ECamType.Webcam &&
  !!cameraDevice?.Name?.trim();

const buildKey = ({ edgeId, deviceName }: TWebcamRelease): string =>
  `${edgeId}|${deviceName}`;

/** Every active webcam configured on an access point, paired with its edge. */
export const getActiveWebcamReferences = (
  accessPoints: AccessPoint[]
): TWebcamRelease[] => {
  const references = (accessPoints ?? [])
    .filter((accessPoint) => !!accessPoint?.EdgeId)
    .flatMap((accessPoint) =>
      (accessPoint.CameraDevices ?? [])
        .filter(isActiveWebcam)
        .map((cameraDevice) => ({
          edgeId: accessPoint.EdgeId,
          deviceName: cameraDevice.Name.trim(),
        }))
    );

  const seen = new Set<string>();
  return references.filter((reference) => {
    const key = buildKey(reference);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Resolves which access-point webcams must be released so the browser can open them.
 *
 * `deviceLabels` are `MediaDeviceInfo.label` values. The edge agent reports a webcam as
 * `"{FriendlyName} (vid:pid)"` — the same shape Chrome uses — so an exact match is reliable
 * whenever a label is available.
 *
 * The browser only exposes labels after camera permission has been granted for the origin.
 * Before that every label is an empty string, and matching by name would release nothing —
 * leaving the device locked by the edge, so `getUserMedia` fails, so permission is never
 * granted, so the labels stay empty. With no usable label the only way out of that loop is
 * to release every active webcam.
 */
export const resolveWebcamsToRelease = (
  accessPoints: AccessPoint[],
  deviceLabels: string[]
): TWebcamRelease[] => {
  const references = getActiveWebcamReferences(accessPoints);
  const usableLabels = (deviceLabels ?? [])
    .map((label) => label?.trim())
    .filter((label): label is string => !!label);

  if (usableLabels.length === 0) {
    return references;
  }

  return references.filter((reference) =>
    usableLabels.some((label) => label === reference.deviceName)
  );
};
